<template>
  <div class="flex flex-col min-h-0">

    <!-- Project context banner -->
    <div v-if="store.addToProjectMode" class="px-4 py-2.5 flex items-center gap-2 text-sm"
         style="background:#0c1a0e;border-bottom:1px solid rgba(74,222,128,0.2);">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#86efac" stroke-width="2.5"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
      <div class="min-w-0">
        <div class="text-green-300 font-medium truncate">{{ $t('items.addingTo') }}: {{ store.addToProjectProject?.name }}</div>
        <div v-if="projectAvailabilityDates" class="text-xs text-green-200/70">{{ $t('bundles.availabilityWindow', { dates: projectAvailabilityDates }) }}</div>
      </div>
    </div>

    <!-- Filter bar -->
    <div v-if="isManagerOrAbove && !store.addToProjectMode" class="px-4 pt-3">
      <router-link to="/bulk-edit/bundles" class="btn ghost small">{{ $t('bulkEdit.title') }}</router-link>
    </div>
    <div class="sticky z-30 px-4 py-3 flex items-center gap-2" style="top:92px; background:rgba(11,15,17,0.92); backdrop-filter:blur(8px); border-bottom:1px solid rgba(255,255,255,0.05);">
      <div class="flex-1">
        <SearchField v-model="filter" :debounce="0" :placeholder="$t('bundles.filterPlaceholder')" />
      </div>
      <button
        @click="availOnly = !availOnly"
        class="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium flex-shrink-0 border cursor-pointer transition-all"
        :style="availOnly ? 'background:#14532d; color:#86efac; border-color:#16a34a;' : 'background:#1f2937; color:#9ca3af; border-color:#374151;'"
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="20 6 9 17 4 12"/></svg>
        {{ $t('bundles.available') }}
      </button>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="px-4 pt-4 space-y-3">
      <div v-for="i in 6" :key="i" class="h-16 rounded-xl bg-white/5 animate-pulse" />
    </div>

    <!-- Error -->
    <div v-else-if="loadError" class="flex flex-col items-center justify-center py-20 text-center px-8">
      <p class="text-red-400 font-medium">{{ loadError }}</p>
    </div>

    <!-- Empty -->
    <div v-else-if="!filteredAll.length" class="flex flex-col items-center justify-center py-20 text-center px-8">
      <MdiPackageVariant class="w-16 h-16 text-gray-500 mb-4" />
      <p class="text-gray-300 font-medium">{{ filter ? $t('bundles.noMatch') : $t('bundles.noBundles') }}</p>
      <p class="text-gray-500 text-sm mt-1">{{ filter ? $t('bundles.tryDifferent') : $t('bundles.createFirst') }}</p>
    </div>

    <!-- Bundle list -->
    <ul v-else class="px-4 py-3 space-y-2">
      <li
        v-for="bundle in filtered"
        :key="bundle.id"
        @click="onRowClick(bundle, $event)"
        class="flex items-center gap-3 p-3 rounded-xl transition-transform"
        :class="store.addToProjectMode ? 'cursor-default' : 'cursor-pointer active:scale-[0.98]'"
        style="background:#0f1720; border:1px solid rgba(255,255,255,0.06);"
      >
        <!-- Availability indicator -->
        <div
          class="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
          :class="bundle.is_available === 'true' || bundle.is_available === true
            ? 'bg-green-900/40 border border-green-800/30'
            : 'bg-red-900/40 border border-red-800/30'"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"
            :class="bundle.is_available === 'true' || bundle.is_available === true ? 'text-green-300' : 'text-red-400'"
          >
            <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
            <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
          </svg>
        </div>

        <!-- Info -->
        <div class="flex-1 min-w-0">
          <div class="text-sm font-medium text-white truncate">{{ bundle.name }}</div>
          <div class="flex items-center gap-2 mt-0.5 flex-wrap">
            <span class="text-xs text-gray-400">SKU: {{ bundle.sku }}</span>
            <span v-if="bundle.items?.length" class="text-xs text-gray-500">· {{ $t('bundles.itemCount', { count: bundle.items.length }) }}</span>
            <span v-if="bundle.total_price" class="text-xs text-gray-500">· ${{ bundle.total_price }}</span>
          </div>
        </div>

        <!-- Availability count -->
        <div class="text-right shrink-0">
          <div class="text-xs font-bold" :class="bundle.is_available === 'true' || bundle.is_available === true ? 'text-green-400' : 'text-red-400'">
            {{ bundle.available_count ?? '—' }}
          </div>
          <div class="text-xs text-gray-600">{{ $t('bundles.availLabel') }}</div>
        </div>

        <!-- Checkout button (project browse mode) -->
        <button
          v-if="store.addToProjectMode"
          @click.stop.prevent="checkoutBundle(bundle)"
          :disabled="checkingOutId === bundle.id"
          class="shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors"
          style="background:#14532d;color:#86efac;border:none;"
        >
          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><path d="m20 6-11 11-5-5"/></svg>
          {{ checkingOutId === bundle.id ? '…' : $t('bundles.checkout') }}
        </button>
        <!-- Add to checkout list button -->
        <button
          v-else
          @click.stop.prevent="addToCheckout(bundle)"
          class="shrink-0 flex items-center justify-center w-[44px] h-[44px] -m-1.5 rounded-full bg-emerald-800 text-emerald-200 border-none cursor-pointer transition-all hover:bg-emerald-700"
          :title="$t('items.addToCheckout')"
          :aria-label="$t('items.addToCheckout')"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path d="M12 5v14M5 12h14"/></svg>
        </button>
        <svg v-if="!store.addToProjectMode" class="shrink-0 text-gray-600" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="m9 18 6-6-6-6"/></svg>
      </li>
    </ul>

    <!-- Pagination -->
    <div v-if="totalPages > 1" class="flex items-center justify-center gap-3 px-4 py-4 text-sm">
      <button
        class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
        style="background:#1f2937; color:#9ca3af; border-color:#374151;"
        :disabled="currentPage === 1"
        @click="changePage(currentPage - 1)"
      >← {{ $t('common.prev') }}</button>
      <span style="color:#6b7280; font-size:.75rem;">{{ currentPage }} / {{ totalPages }}</span>
      <button
        class="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
        style="background:#1f2937; color:#9ca3af; border-color:#374151;"
        :disabled="currentPage >= totalPages"
        @click="changePage(currentPage + 1)"
      >{{ $t('common.next') }} →</button>
    </div>

    <!-- FAB -->
    <router-link
      v-if="isManagerOrAbove"
      to="/bundles/new"
      class="fixed bottom-20 right-4 w-14 h-14 rounded-full flex items-center justify-center shadow-lg z-40 active:scale-90 transition-transform"
      style="background:linear-gradient(180deg,#0A531E,#083d16); border:1px solid rgba(255,255,255,0.1);"
      :aria-label="$t('bundles.new')"
    >
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="white" stroke-width="2.5"><path d="M12 5v14M5 12h14"/></svg>
    </router-link>

  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from '@/stores/store'
import { useToast } from '@/composables/useToast'
import { usePermissions } from '@/composables/usePermissions.js'
import { getBundles } from '@/api/api.js'
import MdiPackageVariant from '~icons/mdi/package-variant'
import SearchField from '@/components/search/SearchField.vue'
import { useI18n } from 'vue-i18n'

const router = useRouter()
const store  = useStore()
const toast  = useToast()
const { isManagerOrAbove } = usePermissions()
const { t, locale } = useI18n()
const filter      = ref('')
const availOnly   = ref(false)
const currentPage = ref(1)
const pageSize    = 20
const bundles     = ref([])
const totalCount  = ref(0)
const loading     = ref(false)
const loadError   = ref(null)

const projectAvailabilityDates = computed(() => {
  const { start_time: start, end_time: end } = store.addToProjectProject ?? {}
  if (!start || !end) return ''
  const formatter = new Intl.DateTimeFormat(locale.value, { dateStyle: 'medium' })
  return `${formatter.format(new Date(start))} – ${formatter.format(new Date(end))}`
})

const filteredAll = computed(() => {
  return bundles.value
})

const totalPages = computed(() => Math.ceil(totalCount.value / pageSize))

const filtered = computed(() => filteredAll.value)

watch([filter, availOnly], () => {
  currentPage.value = 1
  load()
})

async function load() {
  loading.value   = true
  loadError.value = null
  try {
    const params = {
      page: currentPage.value,
      page_size: pageSize,
      search: filter.value.trim(),
    }
    if (availOnly.value) params.available = true
    const project = store.addToProjectProject
    if (project?.start_time && project?.end_time) {
      params.available_from = project.start_time
      params.available_to = project.end_time
      params.exclude_project = project.id
    }
    const res = await getBundles(params)
    bundles.value = res.results ?? res ?? []
    totalCount.value = res.count ?? bundles.value.length
  } catch {
    loadError.value = t('bundles.loadFailed')
  } finally {
    loading.value = false
  }
}

function changePage(page) {
  if (page < 1 || page > totalPages.value || page === currentPage.value) return
  currentPage.value = page
  load()
}

function onRowClick(bundle, event) {
  if (store.addToProjectMode) return
  // Don't navigate if click originated from a button (checkout/add)
  if (event.target.closest('button')) return
  router.push(`/bundles/${bundle.id}`)
}

const checkingOutId = ref(null)

function checkoutBundle(bundle) {
  if (!store.addToProjectProject?.id) return
  store.addCheckoutBundle(bundle)
  router.push({ path: '/checkout/', query: { project: store.addToProjectProject.id, plan: '1' } })
}

function addToCheckout(bundle) {
  store.addCheckoutBundle({
    id: bundle.id,
    name: bundle.name,
    sku: bundle.sku,
    itemCount: bundle.items?.length ?? 0,
    availableCount: bundle.available_count ?? null,
  })
  toast.success(t('bundles.addedToCheckoutToast', { name: bundle.name }))
}

onMounted(load)
</script>
